import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaBars,
  FaTachometerAlt,
  FaTractor,
  FaList,
  FaPlus,
  FaUserCircle,
  FaSignOutAlt,
  FaUsersCog,
  FaTimes,
} from "react-icons/fa";

const AppLayout = ({ username }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("username");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const closeSidebar = () => setSidebarOpen(false);

  const navLinkBaseClass = `nav-link d-flex align-items-center text-white px-2 py-2 ${
    isSidebarCollapsed ? "justify-content-center" : "gap-2"
  }`;

  return (
    <div className="d-flex flex-column flex-md-row" style={{ minHeight: "100vh" }}>
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none"
          style={{ zIndex: 1030 }}
          onClick={closeSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`bg-dark text-white p-3 d-flex flex-column justify-content-between position-fixed top-0 start-0 h-100 ${
          sidebarOpen ? "d-block" : "d-none"
        } d-md-flex ${isSidebarCollapsed ? "collapsed" : ""}`}
        style={{ width: isSidebarCollapsed ? "70px" : "250px", zIndex: 1040 }}
      >
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-md-none">
              <button className="btn btn-outline-light btn-sm" onClick={closeSidebar}>
                <FaTimes />
              </button>
            </div>
            <div className="d-none d-md-block ms-auto">
              <button
                className="btn btn-outline-light btn-sm"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                title={isSidebarCollapsed ? "Rozwiń menu" : "Zwiń menu"}
              >
                {isSidebarCollapsed ? "➡️" : "⬅️"}
              </button>
            </div>
          </div>

          <div className="text-center mb-3">
            <FaTractor size={32} className="mb-1" />
            {!isSidebarCollapsed && <h4>Automonster</h4>}
          </div>

          {username && user && (
            <div className="text-center mb-4">
              <FaUserCircle size={28} className="mb-1" />
              {!isSidebarCollapsed && (
                <div className="small text-white-50">
                  {user.first_name || ""} {user.last_name || ""}
                </div>
              )}
            </div>
          )}

          <ul className="nav flex-column">
            <li className="nav-item">
              <NavLink
                to="/profile"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `${navLinkBaseClass} ${isActive ? "active" : ""}`
                }
                title="Mój profil"
              >
                <FaUserCircle />
                {!isSidebarCollapsed && <span>Mój profil</span>}
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `${navLinkBaseClass} ${isActive ? "active" : ""}`
                }
                title="Dashboard"
              >
                <FaTachometerAlt />
                {!isSidebarCollapsed && <span>Dashboard</span>}
              </NavLink>
            </li>

            {user?.role === "admin" && (
              <li className="nav-item">
                <NavLink
                  to="/admin/users"
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    `${navLinkBaseClass} ${isActive ? "active" : ""}`
                  }
                  title="Zarządzanie użytkownikami"
                >
                  <FaUsersCog />
                  {!isSidebarCollapsed && <span>Zarządzanie użytkownikami</span>}
                </NavLink>
              </li>
            )}

            {!isSidebarCollapsed && (
              <li className="nav-item mt-3">
                <span className="text-white-50 small">Produkty</span>
              </li>
            )}
            <li className="nav-item">
              <NavLink
                to="/products"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `${navLinkBaseClass} ${isActive ? "active" : ""}`
                }
                title="Lista produktów"
              >
                <FaList />
                {!isSidebarCollapsed && <span>Lista produktów</span>}
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/products/add"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `${navLinkBaseClass} ${isActive ? "active" : ""}`
                }
                title="Dodaj produkt"
              >
                <FaPlus />
                {!isSidebarCollapsed && <span>Dodaj produkt</span>}
              </NavLink>
            </li>

            {!isSidebarCollapsed && (
              <li className="nav-item mt-3">
                <span className="text-white-50 small">Raporty</span>
              </li>
            )}
            <li className="nav-item">
              <NavLink
                to="/reports"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `${navLinkBaseClass} ${isActive ? "active" : ""}`
                }
                title="Lista raportów"
              >
                📑
                {!isSidebarCollapsed && <span>Lista raportów</span>}
              </NavLink>
            </li>
          </ul>
        </div>

        <div className="text-center mt-4">
          <button
            onClick={() => {
              handleLogout();
              closeSidebar();
            }}
            className="btn btn-outline-light btn-sm d-flex align-items-center gap-2 mx-auto"
            title="Wyloguj"
          >
            <FaSignOutAlt />
            {!isSidebarCollapsed && <span>Wyloguj</span>}
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-grow-1 content-container"
        style={{
          marginTop: "56px",
          marginLeft: windowWidth >= 768 ? (isSidebarCollapsed ? "70px" : "250px") : "0",
          transition: "margin-left 0.3s ease",
        }}
      >
        <nav className="navbar navbar-light bg-light px-3 fixed-top d-md-none shadow-sm">
          <button className="btn btn-outline-dark" onClick={() => setSidebarOpen(true)}>
            <FaBars />
          </button>
          <span className="navbar-brand mx-auto">Wycena produktów</span>
        </nav>

        <div className="p-4">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
